import { UNIT_SLUGS, emptyUnitMap, normalizeUnitMap } from "./unitDisplay";
import {
  CELL_MAX,
  cloneCells,
  findCell,
  remainingBySlug,
  sortCells,
  withoutCell,
} from "./defenseFormation";

export const BATTLE_ROWS = 20;
export const BATTLE_COLS = 10;
export const DEFENDER_ROWS = 10;
export const ATTACKER_ROW_MIN = 10;
export const BATTLE_DEPLOY_ORDER = [
  "defender",
  "melee",
  "ranged",
  "scout",
  "support",
];

export function isAttackerCell(row, col) {
  const r = Math.floor(Number(row));
  const c = Math.floor(Number(col));
  return (
    Number.isFinite(r) &&
    Number.isFinite(c) &&
    r >= ATTACKER_ROW_MIN &&
    r < BATTLE_ROWS &&
    c >= 0 &&
    c < BATTLE_COLS
  );
}

export function isDefenderCell(row, col) {
  const r = Math.floor(Number(row));
  const c = Math.floor(Number(col));
  return (
    Number.isFinite(r) &&
    Number.isFinite(c) &&
    r >= 0 &&
    r < DEFENDER_ROWS &&
    c >= 0 &&
    c < BATTLE_COLS
  );
}

export function battleGridCells() {
  const list = [];
  for (let row = 0; row < BATTLE_ROWS; row += 1) {
    for (let col = 0; col < BATTLE_COLS; col += 1) {
      list.push({
        row,
        col,
        isDefender: row < DEFENDER_ROWS,
        isAttacker: row >= ATTACKER_ROW_MIN,
        isWall: row === 0,
      });
    }
  }
  return list;
}

export function cellKey(row, col) {
  return `${row},${col}`;
}

export function unionRevealed(lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists || []) {
    for (const entry of list || []) {
      let row;
      let col;
      if (Array.isArray(entry) && entry.length >= 2) {
        row = Math.floor(Number(entry[0]));
        col = Math.floor(Number(entry[1]));
      } else if (entry && typeof entry === "object") {
        row = Math.floor(Number(entry.row));
        col = Math.floor(Number(entry.col));
      } else {
        continue;
      }
      if (!isDefenderCell(row, col)) continue;
      const key = cellKey(row, col);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ row, col });
    }
  }
  return out;
}

/**
 * Select-then-click placement on attacker rows 10–19.
 * No gate. Max 100/cell. Remaining is capped by stocks (idle units).
 */
export function placeAttackStack(cells, row, col, selectedSlug, placeQty, stocks) {
  if (!isAttackerCell(row, col)) {
    return { cells: sortCells(cells), changed: false };
  }

  const current = findCell(cells, row, col);
  const qty = Math.max(0, Math.floor(Number(placeQty) || 0));
  const slug = selectedSlug ? String(selectedSlug).toLowerCase() : null;

  if (!slug) {
    if (!current) return { cells: sortCells(cells), changed: false };
    return { cells: sortCells(withoutCell(cells, row, col)), changed: true };
  }

  if (!UNIT_SLUGS.includes(slug)) {
    return { cells: sortCells(cells), changed: false };
  }

  if (current && current.unit_slug !== slug) {
    const without = withoutCell(cells, row, col);
    const remaining = remainingBySlug(without, stocks);
    const add = Math.min(qty, CELL_MAX, remaining[slug] || 0);
    if (add < 1) return { cells: sortCells(cells), changed: false };
    without.push({ row, col, unit_slug: slug, quantity: add });
    return { cells: sortCells(without), changed: true };
  }

  const currentQty =
    current && current.unit_slug === slug
      ? Math.max(0, Math.floor(Number(current.quantity) || 0))
      : 0;
  const remaining = remainingBySlug(cells, stocks);
  const add = Math.min(qty, CELL_MAX - currentQty, remaining[slug] || 0);
  if (add < 1) return { cells: sortCells(cells), changed: false };
  const next = withoutCell(cells, row, col);
  next.push({ row, col, unit_slug: slug, quantity: currentQty + add });
  return { cells: sortCells(next), changed: true };
}

export function peelAttackStack(cells, row, col, qty) {
  const current = findCell(cells, row, col);
  const n = Math.floor(Number(qty) || 0);
  if (!current || n < 1) {
    return { cells: sortCells(cells), changed: false };
  }
  const nextQty = Math.floor(Number(current.quantity) || 0) - n;
  if (nextQty <= 0) {
    return { cells: sortCells(withoutCell(cells, row, col)), changed: true };
  }
  return {
    cells: sortCells(
      withoutCell(cells, row, col).concat([
        {
          row: current.row,
          col: current.col,
          unit_slug: current.unit_slug,
          quantity: nextQty,
        },
      ])
    ),
    changed: true,
  };
}

export function cloneAttackCells(cells) {
  return cloneCells(cells);
}

export function emptyIdleMap() {
  return emptyUnitMap();
}

export function playbackEventsFromLog(log) {
  if (Array.isArray(log)) return log;
  if (log && Array.isArray(log.events)) return log.events;
  return [];
}

export function groupBattleLogByTick(log) {
  const groups = [];
  for (const event of playbackEventsFromLog(log)) {
    const tick = Math.floor(Number(event?.tick) || 0);
    const last = groups[groups.length - 1];
    if (!last || last.tick !== tick) {
      groups.push({ tick, events: [event] });
    } else {
      last.events.push(event);
    }
  }
  return groups;
}

export function applyBattleEvent(units, event) {
  const list = Array.isArray(units) ? units : [];
  if (!event || typeof event !== "object") return list;
  if (event.type === "move") {
    const row = Math.floor(Number(event.to?.r));
    const col = Math.floor(Number(event.to?.c));
    return list.map((unit) =>
      unit.id === event.unitId ? { ...unit, row, col } : unit
    );
  }
  if (event.type === "attack") {
    const killed = Math.max(0, Math.floor(Number(event.killed) || 0));
    return list.map((unit) => {
      if (unit.id !== event.toId) return unit;
      const qty = Math.max(0, Math.floor(Number(unit.qty) || 0) - killed);
      return { ...unit, qty };
    });
  }
  return list;
}

export function unitsAtTick(initialUnits, events, tick) {
  const limit = Math.floor(Number(tick) || 0);
  let next = (initialUnits || []).map((unit) => ({ ...unit }));
  for (const event of events || []) {
    const eventTick = Math.floor(Number(event?.tick) || 0);
    if (eventTick > limit) break;
    next = applyBattleEvent(next, event);
  }
  return next;
}

export function gaugesAtTick(ticks, tick) {
  const limit = Math.floor(Number(tick) || 0);
  if (!Array.isArray(ticks) || !ticks.length) return {};
  let found = null;
  for (const entry of ticks) {
    const t = Math.floor(Number(entry?.tick) || 0);
    if (t === limit) return entry.gauges && typeof entry.gauges === "object"
      ? entry.gauges
      : {};
    if (t <= limit) found = entry;
  }
  return found?.gauges && typeof found.gauges === "object" ? found.gauges : {};
}

export function subtractUnitMaps(base, ...deductions) {
  const next = normalizeUnitMap(base);
  for (const source of deductions) {
    const used = normalizeUnitMap(source);
    for (const slug of UNIT_SLUGS) {
      next[slug] = Math.max(0, next[slug] - used[slug]);
    }
  }
  return next;
}
